import { useState, useEffect, useCallback } from 'react';
import { getCachedData, setCachedData, CACHE_KEYS } from '../utils/cacheUtils';
import { safeAsync, logError } from '../utils/errorUtils';
import { markStart, markEnd } from '../utils/performanceUtils';

/**
 * Custom hook for fetching and processing data for a specific electorate
 * 
 * @param {string} electorateName - Name of the electorate to load data for
 * @param {Object} options - Additional options
 * @returns {Object} - { data, loading, error, chartData, refresh }
 */
const useElectorateData = (electorateName, options = {}) => {
  const {
    baseUrl = '',
    timeout = 8000,
    generateChartData = true,
    enableLogging = true
  } = options;
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({
    yearly: null,
    agency: null,
    purpose: null
  });
  
  /**
   * Process raw electorate data to generate chart-friendly formats
   * 
   * @param {Object} electorateData - Raw electorate data
   * @returns {Object} - Processed chart data
   */
  const processChartData = useCallback((electorateData) => {
    if (!electorateData || !electorateData.byYear) {
      return {
        yearly: null,
        agency: null,
        purpose: null
      };
    }
    
    // Start performance measurement
    markStart('process_chart_data');
    
    try {
      // Process yearly data from byYear object directly
      const yearlyChartData = Object.entries(electorateData.byYear || {})
        .map(([yearKey, value]) => {
          // Extract just the first year from the financial year format (e.g., "2019-2020" -> "2019")
          const year = yearKey.split('-')[0];
          return {
            year,
            value: value || 0,
            // Include original key for debugging
            originalKey: yearKey
          };
        })
        .sort((a, b) => parseInt(a.year) - parseInt(b.year));
      
      // Process agency data from byAgency object directly
      const agencyChartData = Object.entries(electorateData.byAgency || {})
        .map(([agency, value]) => ({
          name: agency,
          value: value || 0
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      
      // End performance measurement
      markEnd('process_chart_data', enableLogging);
      
      return {
        yearly: yearlyChartData,
        agency: agencyChartData,
        purpose: null // No purpose data in this format
      };
    } catch (err) {
      logError(err, 'processChartData', { electorateName });
      console.error('Error processing chart data:', err);
      return {
        yearly: null,
        agency: null,
        purpose: null
      };
    }
  }, [electorateName, enableLogging]);
  
  /**
   * Load data for a specific electorate
   */
  const loadElectorateData = useCallback(async (forceRefresh = false) => {
    if (!electorateName) {
      setData(null);
      setLoading(false);
      setError(null);
      setChartData({
        yearly: null,
        agency: null,
        purpose: null
      });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Start performance measurement
      markStart(`load_electorate_${electorateName}`);
      
      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cacheKey = `${CACHE_KEYS.ELECTORATE_DATA}${electorateName}`;
        const cachedData = getCachedData(cacheKey);
        
        if (cachedData) {
          setData(cachedData);
          
          // Generate chart data if needed
          if (generateChartData) {
            const chartData = processChartData(cachedData);
            setChartData(chartData);
          }
          
          setLoading(false);
          
          // End performance measurement
          markEnd(`load_electorate_${electorateName}`, enableLogging);
          
          return cachedData;
        }
      }
      
      // Format the electorate name for the URL
      const formattedName = electorateName.replace(/\s+/g, '_');
      const url = `${baseUrl}/data/electorates/${formattedName}.json`;
      
      // Set up request with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, { 
          signal: controller.signal,
          headers: {
            'Cache-Control': forceRefresh ? 'no-cache, no-store, must-revalidate' : 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to load data for ${electorateName}: ${response.status}`);
        }
        
        const responseData = await response.json();
        
        // Add any additional processing or normalization here
        const processedData = {
          ...responseData,
          totalGrants: responseData.grants?.length || 0,
          lastUpdated: new Date().toISOString()
        };
        
        // Update state
        setData(processedData);
        
        // Generate chart data if requested
        if (generateChartData) {
          const newChartData = processChartData(processedData);
          setChartData(newChartData);
        }
        
        // Cache the fetched data
        setCachedData(`${CACHE_KEYS.ELECTORATE_DATA}${electorateName}`, processedData);
        
        // End performance measurement
        markEnd(`load_electorate_${electorateName}`, enableLogging);
        
        return processedData;
      } catch (err) {
        clearTimeout(timeoutId);
        
        if (err.name === 'AbortError') {
          throw new Error(`Request timed out for ${electorateName}`);
        } else {
          throw err;
        }
      }
    } catch (err) {
      logError(err, 'loadElectorateData', { electorateName });
      setError(err.message || `Error loading data for ${electorateName}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [electorateName, baseUrl, timeout, processChartData, generateChartData, enableLogging]);
  
  // Load electorate data when electorateName changes
  useEffect(() => {
    loadElectorateData();
  }, [electorateName, loadElectorateData]);
  
  // Function to force refresh the data
  const refresh = useCallback(() => {
    return loadElectorateData(true);
  }, [loadElectorateData]);
  
  return {
    data,
    loading,
    error,
    chartData,
    refresh
  };
};

export default useElectorateData; 